import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import styled from "styled-components";
import { Button, Switch, Tooltip, Modal, Form, Input, InputNumber, notification, Table, Flex } from "antd";
import {
  PlusOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  getExtensions,
  changeProductExtensionOrder,
  createProductExtension,
  editProductExtensionWithVariants,
  removeProductExtensionWithVariants,
} from "../../../service";
import { colors, spacing, sizes } from "../../../styles/theme";
import { PriceInput } from "../../atoms/PriceInput";
import { AddExtensionCategoryModal } from "../add-extension-category-modal/AddExtensionCategoryModal";
import { useAdditionsTour } from "../../../contexts/additions-tour-context/useAdditionsTour";
import { useResponsive } from "../../../hooks/useResponsive";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";

const CategoryContainer = styled.div`
  background: ${colors.gray100};
  border-radius: ${sizes.borderRadius};
  border: 1px solid ${colors.gray200};
  padding: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: ${spacing.md};
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
  padding: 0 ${spacing.xxl};
  background: ${colors.gray100};
  border-radius: ${sizes.borderRadius} ${sizes.borderRadius} 0 0;

  @media (max-width: 768px) {
    padding: 0 ${spacing.md};
    border-radius: ${spacing.md} ${spacing.md} 0 0;
  }
`;

const CategoryTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
`;

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 768px) {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }
`;

const CategoryDescription = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  margin-top: 4px;
`;

const CategoryActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xl};
`;

const OrderButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};

  @media (max-width: 768px) {
    display: none;
  }
`;

const ArrowButton = styled(Button)`
  
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    &.edit-button {
      display: none;
    }
  }
`;

const AvailabilitySwitch = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const AvailabilityLabel = styled.span`
  font-size: 13px;
  color: ${colors.textSecondary};
  min-width: 65px;
  text-align: right;

  @media (max-width: 768px) {
    display: none;
  }
`;

const AddButtonText = styled.span`
  .mobile-text {
    display: none;
  }

  @media (max-width: 768px) {
    .desktop-text {
      display: none;
    }
    .mobile-text {
      display: inline;
    }
  }
`;

const AdditionOrderButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableWrapper = styled.div<{ $unifiedHeight?: boolean }>`
  padding: 0 ${spacing.lg} ${spacing.lg} ${spacing.lg};
  ${props => props.$unifiedHeight && `
    max-height: 60vh;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: ${colors.gray50};
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.gray300};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${colors.gray400};
    }
  `}

  @media (max-width: 768px) {
    padding: 0 ${spacing.md} ${spacing.md} ${spacing.md};
  }
`;

const StyledTable = styled(Table) <{ $showHeader?: boolean }>`
  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    background: ${colors.white};
    font-weight: 600;
    border-bottom: 1px solid ${colors.borderLight};
    height: ${props => props.$showHeader ? '80px' : '0'};
    padding: ${props => props.$showHeader ? 'inherit' : '0'};
    line-height: ${props => props.$showHeader ? 'inherit' : '0'};
    overflow: hidden;
    visibility: ${props => props.$showHeader ? 'visible' : 'hidden'};
  }

  .ant-table-tbody > tr > td {
    background: ${colors.white};
    border-bottom: 1px solid ${colors.borderLight};
    height: 80px;
  }

  /* Hover tylko na głównym wierszu (poziom 0), nie na expanded rows */
  .ant-table-tbody > tr.ant-table-row-level-0:hover > td {
    background: ${colors.gray200} !important;
  }

  /* Expanded rows (warianty cenowe) NIE mają hover efektu na sobie */
  .ant-table-tbody > tr.ant-table-expanded-row:hover > td {
    background: ${colors.gray50} !important;
  }

  .ant-table-tbody > tr:first-child > td:first-child {
    border-top-left-radius: ${sizes.borderRadius};
  }

  .ant-table-tbody > tr:first-child > td:last-child {
    border-top-right-radius: ${sizes.borderRadius};
  }

  .ant-table-tbody > tr:last-child > td:first-child {
    border-bottom-left-radius: ${sizes.borderRadius};
  }

  .ant-table-tbody > tr:last-child > td:last-child {
    border-bottom-right-radius: ${sizes.borderRadius};
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-expanded-row > td {
    background: ${colors.gray50};
    padding: 0 !important;
    border-bottom: 1px solid ${colors.borderLight} !important;
  }

  /* Klasa dodawana przez JavaScript dla synchronizacji hover */
  .ant-table-tbody > tr.row-hover > td {
    background: ${colors.gray200} !important;
  }

  @media (max-width: 768px) {
    .ant-table-content {
      overflow-x: auto;
    }

    .ant-table-tbody > tr:first-child > td:first-child {
      border-top-left-radius: ${spacing.md};
    }

    .ant-table-tbody > tr:first-child > td:last-child {
      border-top-right-radius: ${spacing.md};
    }

    .ant-table-tbody > tr:last-child > td:first-child {
      border-bottom-left-radius: ${spacing.md};
    }

    .ant-table-tbody > tr:last-child > td:last-child {
      border-bottom-right-radius: ${spacing.md};
    }
  }
`;

interface AdditionCategorySectionProps {
  category: any;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

// Funkcja pomocnicza do generowania opisu kategorii
const getCategoryDescription = (category: any, t: TFunction): string => {
  const typeLabelKeys: Record<string, string> = {
    SELECT: 'typeSelect',
    MULTISELECT: 'typeMultiselect',
    QUANTITIES: 'typeQuantities',
  };

  const typeName = typeLabelKeys[category.type]
    ? t(`additionCategorySection.${typeLabelKeys[category.type]}`)
    : category.type;
  const min = category.minimumRequiredElements || 0;
  const max = category.maximumRequiredElements || 0;

  if (category.type === 'SELECT') {
    return min > 0
      ? t("additionCategorySection.descriptionRequired", { typeName })
      : t("additionCategorySection.descriptionOptional", { typeName });
  }

  if (max === 0) {
    return t("additionCategorySection.descriptionNoLimit", { typeName, min });
  }

  return t("additionCategorySection.descriptionRange", { typeName, min, max });
};

export const AdditionCategorySection: React.FC<AdditionCategorySectionProps> = ({
  category,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const queryClient = useQueryClient();
  const [additions, setAdditions] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [rowForm] = Form.useForm();
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const { addAdditionBtnRef, isTourOpen, activeTour, tourStep, advanceStep } = useAdditionsTour();
  const { isMobile } = useResponsive();
  // Only the first category's button gets the ref (when tour is active)
  const isFirstWithTour = isTourOpen && activeTour === "create-additions" && !canMoveUp;

  const { isLoading } = useQuery<any, Error>(
    [`/additions-category-${category.id}`],
    () => getExtensions({ categoryId: category.id }),
    {
      onSuccess(data) {
        // Backend zwraca obiekt z właściwością 'extensions'
        if (data?.extensions && Array.isArray(data.extensions)) {
          const mappedAdditions = data.extensions.map((item: any) => ({ ...item, key: item.id.toString() }));
          setAdditions(mappedAdditions);

          // Automatycznie rozwiń wszystkie wiersze z wariantami
          const keysToExpand = mappedAdditions
            .filter((item: any) => item.productExtensionVariant && item.productExtensionVariant.length > 0)
            .map((item: any) => item.key);
          setExpandedRowKeys(keysToExpand);
        } else {
          setAdditions([]);
          setExpandedRowKeys([]);
        }
      },
    }
  );

  const { mutateAsync: changeAdditionOrderMutation } = useMutation(
    changeProductExtensionOrder,
    {
      onSuccess: (data) => {
        if (Array.isArray(data)) {
          setAdditions(data.map((item: any) => ({ ...item, key: item.id.toString() })));
        }
        queryClient.invalidateQueries([`/additions-category-${category.id}`]);
      },
    }
  );

  const { mutateAsync: createAdditionMutation } = useMutation(createProductExtension, {
    onSuccess: () => {
      queryClient.invalidateQueries([`/additions-category-${category.id}`]);
      setIsModalOpen(false);
      form.resetFields();
      notification.success({
        message: t("additionCategorySection.notificationAdded"),
        placement: "bottomRight",
      });
      // Tour: addition created → advance from HIDDEN_ADD step to DONE
      // Desktop: HIDDEN_ADD=7, Mobile: HIDDEN_ADD=8
      const hiddenAddStep = isMobile ? 8 : 7;
      if (isTourOpen && activeTour === "create-additions" && tourStep === hiddenAddStep) {
        setTimeout(() => advanceStep(hiddenAddStep), 400);
      }
    },
    onError: (error: any) => {
      notification.error({
        message: t("additionCategorySection.notificationAddError"),
        description: error?.response?.data?.message || t("additionCategorySection.tryAgain"),
        placement: "bottomRight",
      });
    },
  });

  const { mutateAsync: updateAdditionMutation } = useMutation(editProductExtensionWithVariants, {
    onMutate: async (variables) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries([`/additions-category-${category.id}`]);

      // Snapshot the previous value
      const previousAdditions = [...additions];

      // Optimistically update to the new value
      setAdditions((current) =>
        current.map((add) =>
          add.id === variables.id
            ? {
              ...add,
              ...variables.productExtension,
              // Jeśli są warianty, zaktualizuj je
              productExtensionVariant: variables.variants
                ? add.productExtensionVariant?.map((variant: any) => ({
                  ...variant,
                  priceExtension: variables.variants[variant.id] ?? variant.priceExtension,
                }))
                : add.productExtensionVariant,
            }
            : add
        )
      );

      return { previousAdditions };
    },
    onSuccess: () => {
      // NIE invalidujemy query, aby zachować kolejność
      // Optimistic update już zaktualizował stan lokalny
      setEditingKey("");
      notification.success({
        message: t("additionCategorySection.notificationUpdated"),
        placement: "bottomRight",
      });
    },
    onError: (error: any, _variables, context: any) => {
      // Rollback on error
      if (context?.previousAdditions) {
        setAdditions(context.previousAdditions);
      }
      notification.error({
        message: t("additionCategorySection.notificationUpdateError"),
        description: error?.response?.data?.message || t("additionCategorySection.tryAgain"),
        placement: "bottomRight",
      });
    },
  });

  const { mutateAsync: deleteAdditionMutation } = useMutation(removeProductExtensionWithVariants, {
    onSuccess: () => {
      queryClient.invalidateQueries([`/additions-category-${category.id}`]);
      setEditingKey(""); // Wyczyść tryb edycji po usunięciu
      notification.success({
        message: t("additionCategorySection.notificationDeleted"),
        placement: "bottomRight",
      });
    },
  });


  const handleMoveAdditionUp = async (additionId: number) => {
    const currentIndex = additions.findIndex((add) => add.id === additionId);
    if (currentIndex <= 0) return;

    const newAdditions = [...additions];
    [newAdditions[currentIndex - 1], newAdditions[currentIndex]] = [
      newAdditions[currentIndex],
      newAdditions[currentIndex - 1],
    ];

    const orderedIds = newAdditions.map((add) => add.id);
    await changeAdditionOrderMutation({
      categoryId: category.id,
      order: orderedIds,
    });
  };

  const handleMoveAdditionDown = async (additionId: number) => {
    const currentIndex = additions.findIndex((add) => add.id === additionId);
    if (currentIndex < 0 || currentIndex >= additions.length - 1) return;

    const newAdditions = [...additions];
    [newAdditions[currentIndex], newAdditions[currentIndex + 1]] = [
      newAdditions[currentIndex + 1],
      newAdditions[currentIndex],
    ];

    const orderedIds = newAdditions.map((add) => add.id);
    await changeAdditionOrderMutation({
      categoryId: category.id,
      order: orderedIds,
    });
  };

  const handleToggleAvailability = async (addition: any, checked: boolean) => {
    await updateAdditionMutation({
      id: addition.id,
      productExtension: {
        ...addition,
        enabled: checked, // enabled=true znaczy dostępne (normalna logika)
      },
      variants: addition.variants || [],
    });
  };

  // Nazwy wariantów cenowych: z istniejących dodatków, lub domyślnie ["Cena"]
  const variantNames: string[] = additions.length > 0 && additions[0].productExtensionVariant?.length > 0
    ? additions[0].productExtensionVariant.map((v: any) => v.name)
    : ["Cena"];

  const handleAddAddition = () => {
    form.resetFields();
    // Ustaw domyślne wartości dla typu QUANTITIES
    if (category.type === 'QUANTITIES') {
      form.setFieldsValue({
        minimumRequiredQuantity: 0,
        maximumRequiredQuantity: 10,
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmitAddition = async () => {
    try {
      const values = await form.validateFields();

      // Przygotuj dane podstawowe
      const additionData: any = {
        name: values.name,
        extensionCategoryId: category.id,
      };

      // Dla typu QUANTITIES dodaj minimum i maksimum
      if (category.type === 'QUANTITIES') {
        additionData.minimumRequiredQuantity = values.minimumRequiredQuantity || 0;
        additionData.maximumRequiredQuantity = values.maximumRequiredQuantity || 10;
      }

      // Zbierz ceny dla wariantów cenowych
      const variants: any = {};
      variantNames.forEach((name: string) => {
        const fieldName = `variant-${name}`;
        if (values[fieldName] !== undefined) {
          variants[name] = Math.round(parseFloat(values[fieldName]) * 100);
        }
      });
      if (Object.keys(variants).length > 0) {
        additionData.variants = variants;
      }

      await createAdditionMutation(additionData);
    } catch {
      // validation failed
    }
  };

  const handleOpenCategoryModal = () => {
    setIsCategoryModalOpen(true);
  };


  const isEditing = (record: any) => record.key === editingKey;

  const edit = (record: any) => {
    const obj: any = {};
    record?.productExtensionVariant?.forEach((element: any) => {
      obj["variant-" + element.id] = element.priceExtension;
    });
    rowForm.setFieldsValue({
      name: record.name,
      minimumRequiredQuantity: record.minimumRequiredQuantity,
      maximumRequiredQuantity: record.maximumRequiredQuantity,
      ...obj,
    });
    setEditingKey(record.key);
  };




  const save = async (key: string) => {
    try {
      const row = await rowForm.validateFields();
      const addition = additions.find((item) => item.key === key);

      // Zbierz aktualizacje wariantów w formacie { variantId: priceExtension }
      const variantUpdates: any = {};
      Object.keys(row).forEach((fieldKey) => {
        if (fieldKey.startsWith("variant-")) {
          const variantId = parseInt(fieldKey.replace("variant-", ""));
          variantUpdates[variantId] = row[fieldKey];
        }
      });

      await updateAdditionMutation({
        id: addition.id,
        productExtension: {
          name: row.name,
          minimumRequiredQuantity: row.minimumRequiredQuantity || 0,
          maximumRequiredQuantity: row.maximumRequiredQuantity || 0,
        },
        variants: variantUpdates,
      });
    } catch {
      // validation failed
    }
  };

  const handleDelete = (record: any) => {
    Modal.confirm({
      title: t("additionCategorySection.deleteConfirmTitle"),
      content: t("additionCategorySection.deleteConfirmContent"),
      okText: t("additionCategorySection.delete"),
      okType: "danger",
      cancelText: t("additionCategorySection.cancel"),
      onOk: async () => {
        await deleteAdditionMutation({ id: record.id });
      },
    });
  };

  const expandedRowRender = (record: any) => {
    const editMode = isEditing(record);

    if (!record.productExtensionVariant || record.productExtensionVariant.length === 0) {
      return null;
    }

    return (
      <div
        style={{
          background: colors.gray50,
          padding: `${spacing.sm} ${spacing.lg}`,
          margin: 0,
          cursor: editMode ? 'default' : 'pointer',
        }}
        onClick={async () => {
          if (!editMode) {
            // Jeśli kliknięto warianty podczas edycji innego wiersza
            if (editingKey && editingKey !== record.key) {
              await save(editingKey);
            }
            // Włącz edycję tego wiersza
            if (!isEditing(record)) {
              edit(record);
            }
          }
        }}
      >
        <div style={{
          fontSize: '13px',
          fontWeight: 600,
          marginBottom: spacing.xs,
          color: colors.textSecondary
        }}>
          {t("additionCategorySection.priceVariants")}
        </div>
        <Flex vertical gap={0}>
          {record.productExtensionVariant
            ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
            ?.map((e: any, index: number) => {
              return (
                <Flex
                  key={e.id}
                  justify="space-between"
                  align="center"
                  style={{
                    padding: editMode ? `${spacing.xs} 0` : `${spacing.sm} 0`,
                    borderTop: index > 0 ? `1px solid ${colors.borderLight}` : 'none',
                    minHeight: editMode ? 'auto' : '44px',
                  }}
                >
                  <span style={{ fontSize: '14px', color: colors.textSecondary }}>{e.name}</span>
                  {editMode ? (
                    <Form.Item name={`variant-${e.id}`} style={{ margin: 0 }}>
                      <PriceInput
                        value={e.priceExtension ?? 0}
                        onChange={() => { }}
                        editMode={editMode}
                        currency={currentBranch?.currency}
                      />
                    </Form.Item>
                  ) : (
                    <span style={{ fontWeight: 500, fontSize: '14px' }}>
                      {((e.priceExtension ?? 0) / 100).toFixed(2)} {currencySymbol(currentBranch?.currency)}
                    </span>
                  )}
                </Flex>
              );
            })}
        </Flex>
      </div>
    );
  };

  // Dynamiczne kolumny w zależności od typu kategorii
  const getColumns = () => {
    const baseColumns: any[] = [
      {
        title: t("additionCategorySection.columnName"),
        dataIndex: "name",
        key: "name",
        render: (text: string, record: any) => {
          const editable = isEditing(record);
          return editable ? (
            <Form.Item
              name="name"
              style={{ margin: 0 }}
              rules={[{ required: true, message: t("additionCategorySection.ruleName") }]}
            >
              <Input />
            </Form.Item>
          ) : (
            <span>{text}</span>
          );
        },
      },
    ];

    // Tylko dla typu QUANTITIES pokazujemy kolumny Minimum i Maksimum
    if (category.type === 'QUANTITIES') {
      baseColumns.push(
        {
          title: t("additionCategorySection.columnMinimum"),
          dataIndex: "minimumRequiredQuantity",
          key: "minimumRequiredQuantity",
          width: 100,
          render: (value: number, record: any) => {
            const editable = isEditing(record);
            return editable ? (
              <Form.Item
                name="minimumRequiredQuantity"
                style={{ margin: 0 }}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            ) : (
              <span>{value || 0}</span>
            );
          },
        },
        {
          title: t("additionCategorySection.columnMaximum"),
          dataIndex: "maximumRequiredQuantity",
          key: "maximumRequiredQuantity",
          width: 120,
          render: (value: number, record: any) => {
            const editable = isEditing(record);
            return editable ? (
              <Form.Item
                name="maximumRequiredQuantity"
                style={{ margin: 0 }}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder={t("additionCategorySection.placeholderNoLimit")} />
              </Form.Item>
            ) : (
              <span>{value || 0} {value === 0 && t("additionCategorySection.noLimitLabel")}</span>
            );
          },
        }
      );
    }

    // Kolumna akcji - switch i strzałki razem na prawej stronie
    baseColumns.push({
      title: "",
      key: "actions",
      width: 240,
      align: "right" as const,
      render: (_: any, record: any, index: number) => {
        const editable = isEditing(record);

        if (editable) {
          return (
            <Flex gap={8} justify="flex-end">
              <Button
                type="primary"
                onClick={() => save(record.key)}
              >
                {t("additionCategorySection.save")}
              </Button>
              <Tooltip title={t("additionCategorySection.delete")}>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            </Flex>
          );
        }

        return (
          <Flex gap={spacing.xl} justify="flex-end" align="center" onClick={(e) => e.stopPropagation()}>
            <AvailabilitySwitch>
              <AvailabilityLabel>{t("additionCategorySection.available")}</AvailabilityLabel>
              <Switch
                checked={record.enabled}
                onChange={(checked) => handleToggleAvailability(record, checked)}
              />
            </AvailabilitySwitch>

            <AdditionOrderButtonsGroup>
              <Tooltip title={t("additionCategorySection.moveUp")}>
                <ArrowButton
                  type="text"
                  icon={<CaretUpOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveAdditionUp(record.id);
                  }}
                  disabled={index === 0}
                />
              </Tooltip>
              <Tooltip title={t("additionCategorySection.moveDown")}>
                <ArrowButton
                  type="text"
                  icon={<CaretDownOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveAdditionDown(record.id);
                  }}
                  disabled={index === additions.length - 1}
                />
              </Tooltip>
            </AdditionOrderButtonsGroup>
          </Flex>
        );
      },
    });

    return baseColumns;
  };

  // Calculate if we need unified height with scroll
  // Use 60vh if content is large, otherwise let it fit content naturally
  const totalRows = additions.reduce((acc, add) => {
    // Count main row (1) + expanded rows if variants exist
    const hasVariants = add.productExtensionVariant && add.productExtensionVariant.length > 0;
    return acc + (hasVariants ? 2 : 1); // Simplified: 1 main + 1 expanded area
  }, 0);

  // Enable unified height if there are many rows (would exceed ~5 items worth of space)
  const useUnifiedHeight = totalRows > 5;

  // Handle hover synchronization between main row and expanded row
  React.useEffect(() => {
    const handleMouseOver = (e: Event) => {
      const target = e.target as HTMLElement;
      const expandedRow = target.closest('tr.ant-table-expanded-row');

      if (expandedRow) {
        // Hover na expanded row - dodaj klasę tylko do poprzedniego wiersza (główny wiersz tego dodatku)
        const prevRow = expandedRow.previousElementSibling;
        if (prevRow?.classList.contains('ant-table-row-level-0')) {
          prevRow.classList.add('row-hover');
        }
      }
    };

    const handleMouseOut = () => {
      // Usuń hover ze wszystkich wierszy
      const allRows = document.querySelectorAll('.ant-table-tbody tr.row-hover');
      allRows.forEach(row => row.classList.remove('row-hover'));
    };

    const table = document.querySelector('.ant-table-tbody');
    if (table) {
      table.addEventListener('mouseover', handleMouseOver);
      table.addEventListener('mouseout', handleMouseOut);
    }

    return () => {
      if (table) {
        table.removeEventListener('mouseover', handleMouseOver);
        table.removeEventListener('mouseout', handleMouseOut);
      }
    };
  }, [additions]);

  return (
    <>
      <CategoryContainer>
        <CategoryHeader>
          <CategoryTitleRow>
            <div>
              <CategoryTitle onClick={handleOpenCategoryModal}>
                {category.name}
              </CategoryTitle>
              <CategoryDescription>
                {getCategoryDescription(category, t)}
              </CategoryDescription>
            </div>
            <Tooltip title={t("additionCategorySection.editCategory")}>
              <ArrowButton
                type="text"
                icon={<EditOutlined />}
                onClick={handleOpenCategoryModal}
                style={{ marginTop: -4 }}
                className="edit-button"
              />
            </Tooltip>
          </CategoryTitleRow>
          <CategoryActions>
            <Button
              ref={isFirstWithTour ? (addAdditionBtnRef as any) : undefined}
              type="default"
              size="middle"
              icon={<PlusOutlined />}
              onClick={() => {
                handleAddAddition();
                // Tour: advance from ADD_ADDITION step to HIDDEN_ADD step
                // Desktop: ADD_ADDITION=6, Mobile: ADD_ADDITION=7
                const addAdditionStep = isMobile ? 7 : 6;
                if (isTourOpen && activeTour === "create-additions" && tourStep === addAdditionStep) {
                  setTimeout(() => advanceStep(addAdditionStep), 200);
                }
              }}
            >
              <AddButtonText>
                <span className="desktop-text">{t("additionCategorySection.addAddition")}</span>
                <span className="mobile-text">{t("additionCategorySection.addAdditionShort")}</span>
              </AddButtonText>
            </Button>
            <OrderButtonsGroup>
              <Tooltip title={t("additionCategorySection.moveCategoryUp")}>
                <ArrowButton
                  type="text"
                  icon={<CaretUpOutlined />}
                  onClick={onMoveUp}
                  disabled={!canMoveUp}
                />
              </Tooltip>
              <Tooltip title={t("additionCategorySection.moveCategoryDown")}>
                <ArrowButton
                  type="text"
                  icon={<CaretDownOutlined />}
                  onClick={onMoveDown}
                  disabled={!canMoveDown}
                />
              </Tooltip>
            </OrderButtonsGroup>
          </CategoryActions>
        </CategoryHeader>

        <TableWrapper $unifiedHeight={useUnifiedHeight}>
          <StyledTable
            $showHeader={category.type === 'QUANTITIES'}
            columns={getColumns()}
            dataSource={additions}
            loading={isLoading}
            pagination={false}
            components={{
              body: {
                wrapper: (props: any) => <Form form={rowForm} component={false}><tbody {...props} /></Form>,
              },
            }}
            expandable={{
              expandedRowRender,
              expandedRowKeys: expandedRowKeys,
              onExpandedRowsChange: (keys: any) => setExpandedRowKeys(keys),
              expandRowByClick: false,
              showExpandColumn: false, // Ukryj kolumnę z ikoną +/-
              rowExpandable: (record: any) => {
                const hasVariants = record.productExtensionVariant && record.productExtensionVariant.length > 0;
                return hasVariants;
              },
            }}
            onRow={(record: any) => ({
              onClick: async () => {
                // Jeśli kliknięto na inny wiersz podczas edycji
                if (editingKey && editingKey !== record.key) {
                  // Automatycznie zapisz zmiany poprzedniego wiersza
                  await save(editingKey);
                }
                // Włącz edycję nowego wiersza jeśli nie jest edytowany
                if (!isEditing(record)) {
                  edit(record);
                }
              },
            })}
            locale={{
              emptyText: t("additionCategorySection.emptyText"),
            }}
          />
        </TableWrapper>
      </CategoryContainer>

      <Modal
        title={t("additionCategorySection.modalTitle")}
        open={isModalOpen}
        onOk={handleSubmitAddition}
        onCancel={() => setIsModalOpen(false)}
        okText={t("additionCategorySection.add")}
        cancelText={t("additionCategorySection.cancel")}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t("additionCategorySection.labelName")}
            rules={[{ required: true, message: t("additionCategorySection.ruleNameAddition") }]}
          >
            <Input placeholder={t("additionCategorySection.placeholderName")} autoFocus />
          </Form.Item>

          {/* Dla typu QUANTITIES pokazuj minimum i maksimum */}
          {category.type === 'QUANTITIES' && (
            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item
                name="minimumRequiredQuantity"
                label={t("additionCategorySection.columnMinimum")}
                style={{ flex: 1 }}
                rules={[{ required: true, message: t("additionCategorySection.ruleMinimum") }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>

              <Form.Item
                name="maximumRequiredQuantity"
                label={t("additionCategorySection.columnMaximum")}
                style={{ flex: 1 }}
                rules={[{ required: true, message: t("additionCategorySection.ruleMaximum") }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="10" />
              </Form.Item>
            </div>
          )}

          {/* Pola cenowe dla wariantów */}
          {variantNames.length === 1 && variantNames[0] === "Cena" ? (
            <Form.Item
              name="variant-Cena"
              label={t("additionCategorySection.labelPrice")}
              rules={[{ required: true, message: t("additionCategorySection.rulePrice") }]}
            >
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                addonAfter={currencySymbol(currentBranch?.currency)}
                style={{ textAlign: 'right' }}
                styles={{
                  input: {
                    borderTopRightRadius: '0px !important',
                    borderBottomRightRadius: '0px !important',
                    borderRight: '0 !important',
                  },
                }}
                className="price-input-with-addon"
              />
            </Form.Item>
          ) : (
            <>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                marginTop: spacing.md,
                marginBottom: spacing.sm,
                color: colors.textSecondary
              }}>
                {t("additionCategorySection.pricesForVariants")}
              </div>
              {[...variantNames]
                .sort((a, b) => a.localeCompare(b))
                .map((name: string) => (
                  <Form.Item
                    key={name}
                    name={`variant-${name}`}
                    label={name}
                    rules={[{ required: true, message: t("additionCategorySection.rulePriceForVariant", { name }) }]}
                  >
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      addonAfter={currencySymbol(currentBranch?.currency)}
                      style={{ textAlign: 'right' }}
                      styles={{
                        input: {
                          borderTopRightRadius: '0px !important',
                          borderBottomRightRadius: '0px !important',
                          borderRight: '0 !important',
                        },
                      }}
                      className="price-input-with-addon"
                    />
                  </Form.Item>
                ))}
            </>
          )}
        </Form>
      </Modal>

      <AddExtensionCategoryModal
        open={isCategoryModalOpen}
        onCancel={() => setIsCategoryModalOpen(false)}
        category={category}
      />
    </>
  );
};
